import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { applyOrgAccent } from "../lib/orgAccent";

const AuthContext = createContext({
  user: null,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
  switchOrg: async () => {},
});

const PREVIEW_KEY = "mx_preview_family";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Admin-only "View as <tier>": the whole app sees a copy of the user in that role (family / player / trainer / agent); the backend still sees the admin.
  const [previewTier, setPreviewTierState] = useState(() => { const v = sessionStorage.getItem(PREVIEW_KEY); return v === "1" ? "family" : (v && v !== "0" ? v : null); });
  const setPreviewTier = useCallback((tier) => { sessionStorage.setItem(PREVIEW_KEY, tier || "0"); setPreviewTierState(tier || null); }, []);

  // Re-theme the app whenever the active org changes (login, switch, refresh).
  useEffect(() => {
    if (user && user.orgs && user.active_org_id) {
      const active = user.orgs.find(o => o.id === user.active_org_id);
      applyOrgAccent(active?.accent_color);
    } else {
      applyOrgAccent(null);  // fall back to default gold
    }
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const me = await api.authMe();
      setUser(me);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Activity heartbeat: every 60s while the tab is visible, credit 60s of active time to the user.
  useEffect(() => {
    if (!user) return;
    const tick = () => { if (document.visibilityState === "visible") api.heartbeat(60).catch(() => {}); };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [user]);

  const login = useCallback(async (email, password) => {
    await api.authLogin(email, password);
    // Re-hit /me so we grab the enriched org context in one place
    const me = await api.authMe();
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try { await api.authLogout(); } catch (e) {}
    setUser(false);
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    await api.switchOrg(orgId);
    const me = await api.authMe();
    setUser(me);
    return me;
  }, []);

  const canPreview = !!user && (user.is_super_admin || user.active_org_role === "admin");
  const effectiveUser = canPreview && previewTier ? { ...user, is_super_admin: false, active_org_role: previewTier, role: previewTier, _preview: true } : user;

  return (
    <AuthContext.Provider value={{ user: effectiveUser, realUser: user, canPreview, previewTier: canPreview ? previewTier : null, setPreviewTier, login, logout, refresh, switchOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/** Return the currently-active org's accent color, defaulting to gold. */
export function useOrgAccent() {
  const { user } = useAuth();
  const active = activeOrg(user);
  return active?.accent_color || "#d8b854";
}

/** Active-org admin (role === 'admin' in the currently selected org, or super-admin). */
export function isAdmin(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return user.active_org_role === "admin";
}

/** Super-admin gate (only accounts flagged `is_super_admin`). */
export function isSuperAdmin(user) {
  return !!user && !!user.is_super_admin;
}

/** Family/parent/player user (view-only, sees only granted players). */
export function isFamily(user) {
  if (!user) return false;
  if (user.is_super_admin) return false;
  return user.active_org_role === "family" || user.role === "family";
}

/** Trainer user — read-only across the granted player's page EXCEPT the Workout Log. */
export function isTrainer(user) {
  if (!user) return false;
  if (user.is_super_admin) return false;
  return user.active_org_role === "trainer" || user.role === "trainer";
}

/** Player login — sees the outputs only (read-only, granted players). */
export function isPlayer(user) {
  if (!user || user.is_super_admin) return false;
  return user.active_org_role === "player" || user.role === "player";
}

/** Agent / Coach login — read-only, granted players. */
export function isAgent(user) {
  if (!user || user.is_super_admin) return false;
  return user.active_org_role === "agent" || user.role === "agent";
}

/** Lite login — minimal dashboard (read-only, granted players). */
export function isLite(user) {
  if (!user || user.is_super_admin) return false;
  return user.active_org_role === "lite" || user.role === "lite";
}

/** Weekly Wins add-on — admins always; any other member only when `weekly_wins_access` is on their membership. */
export function hasWeeklyWins(user) {
  if (!user) return false;
  return isAdmin(user) || !!user.active_org_weekly_wins;
}

/** Any per-player-granted role (family / trainer / player / agent / lite) — used for route gating + landing page. */
export function isRestricted(user) {
  return isFamily(user) || isTrainer(user) || isPlayer(user) || isAgent(user) || isLite(user);
}

/** Currently-active org object from the user's memberships. */
export function activeOrg(user) {
  if (!user || !user.orgs) return null;
  return user.orgs.find(o => o.id === user.active_org_id) || null;
}
